'use client';

import React, { createContext, useContext, ReactNode } from 'react';
import { useAudioPlayer } from '@/hooks/useAudioPlayer';

type AudioMode = 'card' | 'global' | null;

interface ConversationSegment {
  speaker: string;
  text: string;
}

export interface PlayOptions {
  mode?: AudioMode;
  withGreeting?: boolean;
  customGreeting?: string; // Custom greeting message (overrides time-based greeting if provided)
  preprocessNumbers?: boolean;
  isConversation?: boolean; // Enable two-person conversation mode
  segments?: ConversationSegment[]; // Pre-parsed conversation segments
}

interface AudioPlayerContextType {
  isPlaying: boolean;
  isPaused: boolean;
  currentText: string | null;
  audioMode: AudioMode;
  currentSpeaker: string | null;
  play: (text: string, modeOrOptions?: AudioMode | PlayOptions, options?: PlayOptions) => void;
  pause: () => void;
  resume: () => void;
  stop: () => void;
  toggle: (text: string, modeOrOptions?: AudioMode | PlayOptions, options?: PlayOptions) => void;
}

const AudioPlayerContext = createContext<AudioPlayerContextType | undefined>(undefined);

export function AudioPlayerProvider({ children }: { children: ReactNode }) {
  const audioPlayer = useAudioPlayer();

  return (
    <AudioPlayerContext.Provider value={audioPlayer}>
      {children}
    </AudioPlayerContext.Provider>
  );
}

export function useAudioPlayerContext() {
  const context = useContext(AudioPlayerContext);
  if (context === undefined) {
    throw new Error('useAudioPlayerContext must be used within an AudioPlayerProvider');
  }
  return context;
}

